"use strict";

if (!customElements.get('product-compare')) {
	customElements.define('product-compare', class ProductCompare extends HTMLElement {
		constructor() {
			super();

			this.selectedProducts = new Map();
			this.minProducts = Number(this.dataset.minProducts || 2);
			this.maxProducts = Number(this.dataset.maxProducts || 4);
			this.notice = '';
			this.noticeTimer = null;
			this.gridObserver = null;
			this.openedBy = null;

			this.rows = [
				{ key: 'price', label: 'Price', always: true },
				{ key: 'available', label: 'Availability', always: true },
				{ key: 'vendor', label: 'Brand' },
				{ key: 'productType', label: 'Product type' },
				{ key: 'sku', label: 'SKU' },
				{ key: 'weight', label: 'Weight' },
				{ key: 'fixture_family', label: 'Fixture family', source: 'features' },
				{ key: 'application', label: 'Application', source: 'features' },
				{ key: 'location_rating', label: 'Location rating', source: 'features' },
				{ key: 'mounting_type', label: 'Mounting', source: 'features' },
				{ key: 'voltage', label: 'Voltage', source: 'features' },
				{ key: 'wattage_range', label: 'Wattage', source: 'features' },
				{ key: 'lumen_range', label: 'Lumens', source: 'features' },
				{ key: 'color_temperature', label: 'Color temperature', source: 'features' },
				{ key: 'certifications', label: 'Certifications', source: 'features' },
				{ key: 'housing_material', label: 'Housing material', source: 'features' },
				{ key: 'emergency_backup', label: 'Emergency backup', source: 'features' },
				{ key: 'self_testing', label: 'Self testing', source: 'features' },
				{ key: 'remote_capable', label: 'Remote capable', source: 'features' },
				{ key: 'exit_sign_type', label: 'Exit sign type', source: 'features' },
				{ key: 'letter_color', label: 'Letter color', source: 'features' },
				{ key: 'face_count', label: 'Face count', source: 'features' },
				{ key: 'emergency_light_type', label: 'Emergency light type', source: 'features' },
				{ key: 'head_count', label: 'Head count', source: 'features' },
				{ key: 'indoor_fixture_type', label: 'Indoor fixture type', source: 'features' },
				{ key: 'outdoor_fixture_type', label: 'Outdoor fixture type', source: 'features' },
				{ key: 'power_source', label: 'Power source', source: 'features' },
				{ key: 'controls', label: 'Controls', source: 'features' },
			];

			this.onInput = this.onInput.bind(this);
			this.onChange = this.onChange.bind(this);
			this.onClick = this.onClick.bind(this);
			this.onKeyUp = this.onKeyUp.bind(this);
			this.syncControls = this.syncControls.bind(this);
		}

		connectedCallback() {
			if (this.initialized) {
				return;
			}

			this.initialized = true;
			this.elements = {
				tray: this.querySelector('[data-product-compare-tray]'),
				count: this.querySelector('[data-product-compare-count]'),
				list: this.querySelector('[data-product-compare-list]'),
				message: this.querySelector('[data-product-compare-message]'),
				clear: this.querySelector('[data-product-compare-clear]'),
				open: this.querySelector('[data-product-compare-open]'),
				modal: this.querySelector('[data-product-compare-modal]'),
				dialog: this.querySelector('[role="dialog"]'),
				content: this.querySelector('[data-product-compare-content]'),
			};

			document.addEventListener('input', this.onInput, true);
			document.addEventListener('change', this.onChange);
			document.addEventListener('click', this.onClick);
			document.addEventListener('keyup', this.onKeyUp);
			this.observeProductGrid();
			this.updateTray();
			this.syncControls();
		}

		disconnectedCallback() {
			document.removeEventListener('input', this.onInput, true);
			document.removeEventListener('change', this.onChange);
			document.removeEventListener('click', this.onClick);
			document.removeEventListener('keyup', this.onKeyUp);

			if (this.gridObserver) {
				this.gridObserver.disconnect();
			}
		}

		observeProductGrid() {
			const container = document.getElementById('ProductGridContainer');

			if (!container) {
				return;
			}

			this.gridObserver = new MutationObserver(this.syncControls);
			this.gridObserver.observe(container, { childList: true, subtree: true });
		}

		onInput(event) {
			const toggle = event.target.closest('[data-product-compare-toggle]');

			if (toggle) {
				event.stopPropagation();
			}
		}

		onChange(event) {
			const toggle = event.target.closest('[data-product-compare-toggle]');

			if (!toggle) {
				return;
			}

			const product = this.readProduct(toggle);

			if (!product) {
				toggle.checked = false;
				this.showNotice('Product details were not available for comparison.');
				return;
			}

			if (toggle.checked) {
				this.addProduct(product, toggle);
			} else {
				this.removeProduct(product.id);
			}
		}

		onClick(event) {
			const target = event.target;
			const removeButton = target.closest('[data-product-compare-remove]');

			if (target.closest('[data-product-compare-open]')) {
				this.openModal();
				return;
			}

			if (target.closest('[data-product-compare-clear]')) {
				this.clearProducts();
				return;
			}

			if (target.closest('[data-product-compare-close]')) {
				this.closeModal();
				return;
			}

			if (removeButton) {
				this.removeProduct(removeButton.dataset.productCompareRemove);
				return;
			}

			if (target === this.elements.modal) {
				this.closeModal();
			}
		}

		onKeyUp(event) {
			if (event.code.toUpperCase() === 'ESCAPE' && this.isModalOpen()) {
				this.closeModal();
			}
		}

		readProduct(toggle) {
			const card = toggle.closest('product-card');
			const data = card ? card.querySelector('[data-product-compare-data]') : null;

			if (!data) {
				return null;
			}

			try {
				const product = JSON.parse(data.textContent);
				product.id = String(product.id || '');
				product.features = product.features || {};
				return product.id ? product : null;
			} catch (error) {
				return null;
			}
		}

		addProduct(product, toggle) {
			if (!this.selectedProducts.has(product.id) && this.selectedProducts.size >= this.maxProducts) {
				toggle.checked = false;
				this.showNotice(`Compare up to ${this.maxProducts} products at a time.`);
				return;
			}

			this.selectedProducts.set(product.id, product);
			this.updateTray();
			this.syncControls();

			if (this.isModalOpen()) {
				this.renderModal();
			}
		}

		removeProduct(productId) {
			this.selectedProducts.delete(String(productId || ''));
			this.updateTray();
			this.syncControls();

			if (!this.isModalOpen()) {
				return;
			}

			if (this.selectedProducts.size >= this.minProducts) {
				this.renderModal();
			} else {
				this.closeModal();
			}
		}

		clearProducts() {
			this.selectedProducts.clear();
			this.updateTray();
			this.syncControls();
			this.closeModal();
		}

		showNotice(message) {
			this.notice = message;
			window.clearTimeout(this.noticeTimer);
			this.noticeTimer = window.setTimeout(() => {
				this.notice = '';
				this.updateTray();
			}, 3500);
			this.updateTray();
		}

		updateTray() {
			const products = this.getSelectedProducts();
			const count = products.length;

			if (!this.elements?.tray) {
				return;
			}

			this.elements.tray.hidden = count === 0;
			this.elements.open.disabled = count < this.minProducts;
			this.elements.count.textContent = `${count} ${count === 1 ? 'product' : 'products'} selected`;
			this.elements.list.innerHTML = products.map((product) => this.renderChip(product)).join('');
			this.elements.message.textContent = this.getMessage(count);
		}

		getMessage(count) {
			if (this.notice) {
				return this.notice;
			}

			if (count === 0) {
				return `Select at least ${this.minProducts} products to compare.`;
			}

			if (count < this.minProducts) {
				const needed = this.minProducts - count;
				return `Select ${needed} more ${needed === 1 ? 'product' : 'products'} to compare.`;
			}

			return `Ready to compare ${count} ${count === 1 ? 'product' : 'products'}.`;
		}

		renderChip(product) {
			const title = this.escapeHTML(product.title);

			return `
				<span class="product-compare__chip">
					<span>${title}</span>
					<button type="button" data-product-compare-remove="${this.escapeAttribute(product.id)}" aria-label="Remove ${title}">
						&times;
					</button>
				</span>
			`;
		}

		syncControls() {
			const selectedCount = this.selectedProducts.size;

			document.querySelectorAll('[data-product-compare-toggle]').forEach((toggle) => {
				const product = this.readProduct(toggle);
				const isSelected = product ? this.selectedProducts.has(product.id) : false;
				const card = toggle.closest('product-card');

				toggle.checked = isSelected;
				toggle.disabled = !isSelected && selectedCount >= this.maxProducts;

				if (card) {
					card.classList.toggle('product-compare-selected', isSelected);
				}
			});
		}

		openModal() {
			if (this.selectedProducts.size < this.minProducts) {
				this.showNotice(`Select at least ${this.minProducts} products to compare.`);
				return;
			}

			this.openedBy = this.elements.open;
			this.renderModal();
			this.elements.modal.hidden = false;
			document.body.classList.add('overflow-hidden');

			if (typeof trapFocus === 'function') {
				trapFocus(this.elements.modal, this.elements.dialog);
			} else {
				this.elements.dialog.focus();
			}
		}

		closeModal() {
			if (!this.elements?.modal || !this.isModalOpen()) {
				return;
			}

			this.elements.modal.hidden = true;
			document.body.classList.remove('overflow-hidden');

			if (typeof removeTrapFocus === 'function') {
				removeTrapFocus(this.openedBy || this.elements.open);
			} else if (this.openedBy) {
				this.openedBy.focus();
			}
		}

		isModalOpen() {
			return this.elements?.modal && !this.elements.modal.hidden;
		}

		renderModal() {
			const products = this.getSelectedProducts();
			const rows = this.rows.filter((row) => row.always || products.some((product) => this.hasValue(this.getValue(product, row))));

			this.elements.content.innerHTML = `
				<table class="product-compare__table">
					<thead>
						<tr>
							<th scope="col">Feature</th>
							${products.map((product) => this.renderProductHeader(product)).join('')}
						</tr>
					</thead>
					<tbody>
						${rows.map((row) => this.renderRow(row, products)).join('')}
					</tbody>
				</table>
			`;
		}

		renderProductHeader(product) {
			const title = this.escapeHTML(product.title);
			const url = this.escapeAttribute(product.url);
			const image = String(product.image || '').trim();

			return `
				<th scope="col">
					<div class="product-compare__product">
						${image ? `<img class="product-compare__product-media" src="${this.escapeAttribute(image)}" alt="${title}" loading="lazy">` : '<div class="product-compare__product-media" aria-hidden="true"></div>'}
						<a class="product-compare__product-title" href="${url}">${title}</a>
						<div class="product-compare__product-price">${this.escapeHTML(product.price || '')}</div>
						<div class="product-compare__product-actions">
							<a class="button button-secondary" href="${url}">View product</a>
							<button type="button" class="product-compare__remove" data-product-compare-remove="${this.escapeAttribute(product.id)}">Remove</button>
						</div>
					</div>
				</th>
			`;
		}

		renderRow(row, products) {
			return `
				<tr>
					<th scope="row">${this.escapeHTML(row.label)}</th>
					${products.map((product) => `<td>${this.renderValue(this.getValue(product, row))}</td>`).join('')}
				</tr>
			`;
		}

		getValue(product, row) {
			const value = row.source === 'features' ? product.features?.[row.key] : product[row.key];

			if (Array.isArray(value)) {
				return value.filter(Boolean).join(', ');
			}

			return String(value || '').trim();
		}

		hasValue(value) {
			return String(value || '').trim().length > 0;
		}

		renderValue(value) {
			if (!this.hasValue(value)) {
				return '<span class="product-compare__value-empty">N/A</span>';
			}

			return this.escapeHTML(value);
		}

		getSelectedProducts() {
			return Array.from(this.selectedProducts.values());
		}

		escapeHTML(value) {
			return String(value || '')
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;')
				.replace(/"/g, '&quot;')
				.replace(/'/g, '&#039;');
		}

		escapeAttribute(value) {
			return this.escapeHTML(value);
		}
	});
}
